import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    FaUsers, FaUserPlus, FaPlusSquare, FaLock, FaUnlock, FaUser, FaTrash,
    FaPen, FaChevronDown, FaChevronUp, FaCalendarAlt,
    FaMapMarkerAlt, FaSignOutAlt as FaLogout,
} from "react-icons/fa";
import { IoEyeOffOutline, IoEyeOutline } from "react-icons/io5";
import { FiCopy, FiSearch, FiChevronDown, FiChevronUp, FiMenu, FiX } from "react-icons/fi";

// ─── DEMO WEDDING INFO ────────────────────────────────────────────────────────

const DEMO_WEDDING = {
    brideName: "Lucia",
    groomName: "Martin",
    date: "2026-09-14T12:00:00.000Z",
    location: "Hotel Palace",
    city: "Zagreb",
    lockReservationDaysBefore: 3,
};

// Demo guest who is "logged in" via the code "test1"
const DEMO_LOGGED_GUEST = {
    _id: "g1",
    fullName: "Thomas Weber",
    gender: "male",
    password: "test1",
    reservedTableId: null,   // starts with no reservation so the demo is interesting
    reservationCancelCount: 0,
    onLockedTable: false,
    weddingId: "demo",
};

// ─── HARDCODED DEMO DATA ──────────────────────────────────────────────────────
// Public tables use simple numeric names ("1", "2", …)
// Private tables use descriptive names that get translated via i18n keys

const DEMO_TABLES_INIT = [
    {
        _id: "t1", name: "1", seats: 8,
        isLocked: false, isPrivateTable: false,
        guests: [
            { _id: "g2", fullName: "Laura Bauer", gender: "female" },
            { _id: "g3", fullName: "Michael Schmidt", gender: "male" },
            { _id: "g4", fullName: "Sophie Müller", gender: "female" },
            { _id: "g5", fullName: "David Fischer", gender: "male" },
        ],
    },
    {
        _id: "t2", name: "2", seats: 6,
        isLocked: false, isPrivateTable: false,
        guests: [
            { _id: "g6", fullName: "Emma Martin", gender: "female" },
            { _id: "g7", fullName: "Lucas Bernard", gender: "male" },
            { _id: "g8", fullName: "Mia Dupont", gender: "female" },
            { _id: "g9", fullName: "Noah Laurent", gender: "male" },
        ],
    },
    {
        _id: "t3", name: "3", seats: 6,
        isLocked: true, isPrivateTable: false,
        guests: [
            { _id: "g10", fullName: "James Wilson", gender: "male" },
            { _id: "g11", fullName: "Olivia Taylor", gender: "female" },
        ],
    },
    {
        _id: "t4", name: "4", seats: 6,
        isLocked: false, isPrivateTable: false,
        guests: [
            { _id: "g14", fullName: "Pierre Dubois", gender: "male" },
            { _id: "g15", fullName: "Anna Jensen", gender: "female" },
        ],
    },
    // Private tables
    {
        _id: "t6", name: "demoPrivateFamily", seats: 8,
        isLocked: true, isPrivateTable: true,
        guests: [
            { _id: "g12", fullName: "Robert Horst", gender: "male" },
            { _id: "g13", fullName: "Ingrid Horst", gender: "female" },
        ],
    }
];

const DEMO_GUESTS_INIT = [
    { _id: "g1", fullName: "Thomas Weber", gender: "male", password: "test1", reservedTableId: null, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g2", fullName: "Laura Bauer", gender: "female", password: "lb3a9", reservedTableId: { _id: "t1", name: "1" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g3", fullName: "Michael Schmidt", gender: "male", password: "m5sc6", reservedTableId: { _id: "t1", name: "1" }, reservationCancelCount: 1, onLockedTable: false, weddingId: "demo" },
    { _id: "g4", fullName: "Sophie Müller", gender: "female", password: "s7mu8", reservedTableId: { _id: "t1", name: "1" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g5", fullName: "David Fischer", gender: "male", password: "d2fi0", reservedTableId: { _id: "t1", name: "1" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g6", fullName: "Emma Martin", gender: "female", password: "e4ma3", reservedTableId: { _id: "t2", name: "2" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g7", fullName: "Lucas Bernard", gender: "male", password: "l9be1", reservedTableId: { _id: "t2", name: "2" }, reservationCancelCount: 2, onLockedTable: false, weddingId: "demo" },
    { _id: "g8", fullName: "Mia Dupont", gender: "female", password: "m1du4", reservedTableId: { _id: "t2", name: "2" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g9", fullName: "Noah Laurent", gender: "male", password: "n7la2", reservedTableId: { _id: "t2", name: "2" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g10", fullName: "James Wilson", gender: "male", password: "j3wi8", reservedTableId: { _id: "t3", name: "3" }, reservationCancelCount: 0, onLockedTable: true, weddingId: "demo" },
    { _id: "g11", fullName: "Olivia Taylor", gender: "female", password: "o6ta5", reservedTableId: { _id: "t3", name: "3" }, reservationCancelCount: 0, onLockedTable: true, weddingId: "demo" },
    { _id: "g12", fullName: "Robert Horst", gender: "male", password: "r0no1", reservedTableId: { _id: "t6", name: "demoPrivateFamily" }, reservationCancelCount: 0, onLockedTable: true, weddingId: "demo" },
    { _id: "g13", fullName: "Ingrid Horst", gender: "female", password: "i2no9", reservedTableId: { _id: "t6", name: "demoPrivateFamily" }, reservationCancelCount: 0, onLockedTable: true, weddingId: "demo" },
    { _id: "g14", fullName: "Pierre Dubois", gender: "male", password: "p8du1", reservedTableId: { _id: "t4", name: "4" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
    { _id: "g15", fullName: "Anna Jensen", gender: "female", password: "a4je7", reservedTableId: { _id: "t4", name: "4" }, reservationCancelCount: 0, onLockedTable: false, weddingId: "demo" },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const cap = (str) =>
    str?.toLowerCase().split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

const getLockDate = (wedding) => {
    const d = new Date(wedding.date);
    d.setDate(d.getDate() - (wedding.lockReservationDaysBefore || 3));
    return d;
};

const canEdit = (wedding) => new Date() < getLockDate(wedding);

// ─── DEMO BANNER ──────────────────────────────────────────────────────────────

const DemoBanner = () => {
    const { t } = useTranslation();
    return (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-md">
            <div className="flex items-center justify-center gap-2 py-2 px-4 text-sm font-medium">
                <span className="hidden sm:inline">🎭 {t("demoBannerText")}</span>
                <span className="sm:hidden">🎭 {t("demoBannerTextShort")}</span>
                <Link to="/" className="underline font-semibold hover:text-white/80 whitespace-nowrap">
                    {t("demoBannerBack")}
                </Link>
            </div>
        </div>
    );
};

// ─── CONFIRM MODAL ────────────────────────────────────────────────────────────

const ConfirmModal = ({ isOpen, message, onConfirm, onClose }) => {
    const { t } = useTranslation();
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm">
                <p className="text-gray-800 text-center mb-6">{message}</p>
                <div className="flex gap-3 justify-end">
                    <button onClick={onClose}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition">
                        {t("cancel")}
                    </button>
                    <button onClick={onConfirm}
                        className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition">
                        {t("confirm")}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── NOTIFICATION ─────────────────────────────────────────────────────────────

const Notification = ({ message, onClose, isError }) => (
    <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 ${isError ? "bg-red-600" : "bg-gray-800"} text-white px-6 py-3 rounded-2xl shadow-xl flex items-center gap-3`}>
        <span>{isError ? "✕" : "✓"}</span>
        <span>{message}</span>
        <button onClick={onClose} className="ml-2 text-white/60 hover:text-white">✕</button>
    </div>
);

// ─── GUEST EDIT MODAL ─────────────────────────────────────────────────────────

const GuestEditModal = ({ guest, tables, onClose, onMove, onDelete }) => {
    const { t } = useTranslation();
    const [selectedTableId, setSelectedTableId] = useState(guest.reservedTableId?._id || "");
    const publicTables = tables.filter(tbl => !tbl.isPrivateTable);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
                <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-800">{cap(guest.fullName)}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                </div>
                <p className="text-sm text-gray-500 mb-1">
                    {t("demoGuestCode")}: <span className="font-mono font-semibold text-gray-700">{guest.password}</span>
                </p>
                <p className="text-sm text-gray-500 mb-4">
                    {t("demoCurrentTable")}:{" "}
                    {guest.reservedTableId
                        ? <span className="text-green-600 font-semibold">{t("tableLabel", { name: guest.reservedTableId.name.startsWith("demoPrivate") ? t(guest.reservedTableId.name) : guest.reservedTableId.name })}</span>
                        : <span className="text-red-500 font-semibold">{t("notReservedLabel")}</span>}
                </p>

                <label className="block text-sm font-medium text-gray-700 mb-1">{t("moveToTable")}</label>
                <select
                    value={selectedTableId}
                    onChange={e => setSelectedTableId(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-pink-300"
                >
                    <option value="">— {t("noReservation")} —</option>
                    {tables.map(tbl => (
                        <option
                            key={tbl._id} value={tbl._id}
                            disabled={tbl.guests.length >= tbl.seats && tbl._id !== guest.reservedTableId?._id}
                        >
                            {t("tableLabel", { name: tbl.name.startsWith("demoPrivate") ? t(tbl.name) : tbl.name })}
                            {tbl.isPrivateTable ? ` (${t("privateTable")})` : ""}
                            ({tbl.guests.length}/{tbl.seats})
                        </option>
                    ))}
                </select>

                <div className="flex gap-3">
                    <button onClick={() => onMove(guest._id, selectedTableId || null)}
                        className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white py-2 rounded-xl hover:from-rose-600 hover:to-pink-700 transition">
                        {t("save")}
                    </button>
                    <button onClick={() => onDelete(guest._id)}
                        className="px-4 py-2 border border-red-300 text-red-500 rounded-xl hover:bg-red-50 transition">
                        <FaTrash />
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── DEMO SIDEBAR ─────────────────────────────────────────────────────────────

const DemoSidebar = ({ activeForm, setActiveForm, guests, tables, isMobileOpen, setIsMobileOpen }) => {
    const { t } = useTranslation();

    const btnCls = (form) =>
        `flex items-center space-x-3 px-4 py-2 text-xl w-full transition ${activeForm === form
            ? "bg-gradient-to-r from-rose-500 to-pink-600 text-white shadow-lg"
            : "text-gray-800 hover:bg-gray-100"
        }`;

    // nav sadržaj bez loga — logo je u topbaru (mobile) i u desktop sidebaru zasebno
    const navLinks = (
        <div className="flex flex-col h-full">
            <div className="flex-1 flex flex-col py-4 space-y-1">
                <button onClick={() => { setActiveForm("guests"); setIsMobileOpen(false); }} className={btnCls("guests")}>
                    <FaUsers /><span>{t("guests")}</span><span className="text-sm ml-auto">{guests.length}</span>
                </button>
                <button onClick={() => { setActiveForm("tables"); setIsMobileOpen(false); }} className={btnCls("tables")}>
                    <FaPlusSquare /><span>{t("tables")}</span><span className="text-sm ml-auto">{tables.length}</span>
                </button>
                <button onClick={() => { setActiveForm("addGuest"); setIsMobileOpen(false); }} className={btnCls("addGuest")}>
                    <FaUserPlus /><span>{t("addGuest")}</span>
                </button>
                <button onClick={() => { setActiveForm("addTable"); setIsMobileOpen(false); }} className={btnCls("addTable")}>
                    <FaPlusSquare /><span>{t("addTable")}</span>
                </button>
            </div>
        </div>
    );

    return (
        <>
            {/* Desktop sidebar — ispod bannera (top-10) + tabova (h-10) = top-20 */}
            <div className="hidden lg:flex flex-col w-72 h-screen fixed top-20 left-0 shadow-lg z-40 border-gray-200">
                <div className="px-4 pt-5 pb-1 border-b border-gray-200 flex items-center gap-2 font-bold text-xl">
                    <img src="/logo2.png" className="w-10 h-10" alt="Weddinfy" />
                    {t("appName")}
                </div>
                {navLinks}
            </div>

            {/* Mobile topbar — ispod bannera (top-10) + tabova (h-10) = top-20 */}
            <div className="lg:hidden fixed top-20 left-0 right-0 z-40 bg-white shadow-sm border-b border-gray-200 px-4 py-2 flex justify-between items-center">
                <div className="flex items-center gap-2 font-semibold text-gray-800">
                    <img src="/logo2.png" className="w-8 h-8" alt="" /> {t("appName")}
                </div>
                <button onClick={() => setIsMobileOpen(v => !v)} className="text-2xl text-gray-700">
                    {isMobileOpen ? <FiX /> : <FiMenu />}
                </button>
            </div >

            {/* Mobile drawer — otvara se ispod topbara */}
            {
                isMobileOpen && (
                    <div className="lg:hidden fixed top-[116px] left-0 right-0 bottom-0 z-40 bg-white shadow-xl overflow-y-auto">
                        {navLinks}
                    </div>
                )
            }
        </>
    );
};

// ─── GUEST LIST ───────────────────────────────────────────────────────────────

const DemoGuestList = ({ guests, tables, setActiveForm, onMove, onDelete, showNotif }) => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedId, setExpandedId] = useState(null);
    const [editGuest, setEditGuest] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [genderFilter, setGenderFilter] = useState("");
    const [reservationFilter, setReservationFilter] = useState("");
    const [tableFilter, setTableFilter] = useState("");
    const [openFilterId, setOpenFilterId] = useState(null);
    const filterRefs = useRef({});

    useEffect(() => {
        const handler = (e) => {
            if (!Object.values(filterRefs.current).some(r => r && r.contains(e.target)))
                setOpenFilterId(null);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const tableName = (tbl) => tbl.name.startsWith("demoPrivate") ? t(tbl.name) : tbl.name;

    const filtered = guests.filter(g => {
        const s = g.fullName.toLowerCase().includes(searchTerm.toLowerCase());
        const gen = genderFilter && genderFilter !== "all" ? g.gender === genderFilter : true;
        const res = reservationFilter === "reserved" ? !!g.reservedTableId
            : reservationFilter === "not_reserved" ? !g.reservedTableId : true;
        const tbl = tableFilter && tableFilter !== "all"
            ? g.reservedTableId?.name === tableFilter : true;
        return s && gen && res && tbl;
    });

    const handleCopy = (guest) => {
        const msg = `${t("demoInviteSalutation")} ${cap(guest.fullName)},\n${t("demoInviteBody")}\n🔗 ${t("weddingLink")}: https://weddinfy.com/organization/demo\n🔑 ${t("verificationCode")}: ${guest.password}`;
        navigator.clipboard.writeText(msg).catch(() => { });
        showNotif(t("copySuccess"));
    };

    const resolveTableLabel = (name) =>
        name?.startsWith("demoPrivate") ? t(name) : name;

    return (
        <div className="flex lg:ml-[300px] lg:mr-2">
            <div className="rounded-md w-full mt-2 pt-10 lg:pt-10">
                <div className="flex justify-between lg:justify-around items-center mb-4">
                    <h1 className="text-3xl font-semibold">{t("guestListTitle")}</h1>
                    <button onClick={() => setActiveForm("addGuest")}
                        className="bg-gradient-to-r from-rose-500 to-pink-600 text-white p-2 rounded-xl shadow-lg hover:from-rose-600 hover:to-pink-700 transition">
                        <FaUserPlus className="text-xl" />
                    </button>
                </div>

                <div className="w-full flex flex-col gap-2 mb-4">
                    <button onClick={() => setShowFilters(v => !v)}
                        className="flex justify-between items-center bg-white border border-gray-500 px-4 py-1 rounded-xl hover:bg-gray-50">
                        {showFilters ? t("closeFilters") : t("openFilters")}
                        {showFilters ? <FiChevronUp className="text-2xl" /> : <FiChevronDown className="text-2xl" />}
                    </button>

                    {showFilters && (
                        <div className="flex flex-col gap-2">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-xl p-4 bg-white border border-gray-500">
                                {/* Gender */}
                                <div className="relative" ref={el => filterRefs.current.gender = el}>
                                    <div onClick={() => setOpenFilterId(v => v === "gender" ? null : "gender")}
                                        className="flex justify-between items-center bg-white border border-gray-500 rounded-xl px-4 py-2 cursor-pointer">
                                        <span>{genderFilter === "male" ? t("male") : genderFilter === "female" ? t("female") : t("gender")}</span>
                                        {openFilterId === "gender" ? <FiChevronUp className="text-xl" /> : <FiChevronDown className="text-xl" />}
                                    </div>
                                    {openFilterId === "gender" && (
                                        <div className="absolute w-full mt-1 bg-white border border-gray-500 rounded-xl shadow-md z-50">
                                            {[["male", t("male")], ["female", t("female")], ["all", t("allGenders")]].map(([v, l]) => (
                                                <button key={v} onClick={() => { setGenderFilter(v); setOpenFilterId(null); }}
                                                    className="px-4 py-2 w-full text-left hover:bg-gray-100">{l}</button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Reservation */}
                                <div className="relative" ref={el => filterRefs.current.res = el}>
                                    <div onClick={() => setOpenFilterId(v => v === "res" ? null : "res")}
                                        className="flex justify-between items-center bg-white border border-gray-500 rounded-xl px-4 py-2 cursor-pointer">
                                        <span>
                                            {reservationFilter === "reserved" ? t("reserved")
                                                : reservationFilter === "not_reserved" ? t("notReserved")
                                                    : t("reservation")}
                                        </span>
                                        {openFilterId === "res" ? <FiChevronUp className="text-xl" /> : <FiChevronDown className="text-xl" />}
                                    </div>
                                    {openFilterId === "res" && (
                                        <div className="absolute w-full mt-1 bg-white border border-gray-500 rounded-xl shadow-md z-50">
                                            {[["reserved", t("reserved")], ["not_reserved", t("notReserved")], ["all", t("allReservations")]].map(([v, l]) => (
                                                <button key={v} onClick={() => { setReservationFilter(v); setOpenFilterId(null); }}
                                                    className="px-4 py-2 w-full text-left hover:bg-gray-100">{l}</button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Table */}
                                <div className="relative" ref={el => filterRefs.current.tbl = el}>
                                    <div onClick={() => setOpenFilterId(v => v === "tbl" ? null : "tbl")}
                                        className="flex justify-between items-center bg-white border border-gray-500 rounded-xl px-4 py-2 cursor-pointer">
                                        <span>{tableFilter && tableFilter !== "all" ? resolveTableLabel(tableFilter) : t("table")}</span>
                                        {openFilterId === "tbl" ? <FiChevronUp className="text-xl" /> : <FiChevronDown className="text-xl" />}
                                    </div>
                                    {openFilterId === "tbl" && (
                                        <div className="absolute w-full mt-1 bg-white border border-gray-500 rounded-xl shadow-md max-h-52 overflow-y-auto z-50">
                                            {tables.map(tbl => (
                                                <button key={tbl._id} onClick={() => { setTableFilter(tbl.name); setOpenFilterId(null); }}
                                                    className="flex items-center gap-2 px-4 py-2 w-full text-left hover:bg-gray-100">
                                                    {t("tableLabel", { name: tableName(tbl) })}
                                                    {tbl.isPrivateTable && <span className="text-xs text-rose-500">{t("privateTable")}</span>}
                                                </button>
                                            ))}
                                            <button onClick={() => { setTableFilter("all"); setOpenFilterId(null); }}
                                                className="px-4 py-2 w-full text-left hover:bg-gray-100">{t("allTables")}</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <button onClick={() => { setGenderFilter(""); setReservationFilter(""); setTableFilter(""); }}
                                className="bg-white border border-gray-500 hover:bg-gray-100 px-6 py-2 rounded-xl transition w-full">
                                {t("resetFilters")}
                            </button>
                        </div>
                    )}

                    <div className="relative">
                        <input type="text" placeholder={t("searchGuests")}
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-white border border-gray-500 rounded-xl px-4 py-2 pl-10 focus:outline-none focus:ring-2 focus:ring-pink-300" />
                        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                    </div>
                    <hr className="bg-gray-300" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 w-full items-start">
                    {filtered.map(guest => (
                        <div key={guest._id} className="bg-white rounded-xl shadow-md border border-gray-300 overflow-hidden">
                            <div onClick={() => setExpandedId(expandedId === guest._id ? null : guest._id)}
                                className="flex justify-between items-center p-4 cursor-pointer hover:bg-blue-50 transition">
                                <h2 className="text-lg font-semibold text-gray-800 truncate">{cap(guest.fullName)}</h2>
                                <div className="flex items-center gap-2 ml-2">
                                    <div className={`w-2 h-2 rounded-full ${guest.reservedTableId ? "bg-green-500" : "bg-red-500"}`} />
                                    <svg className={`w-6 h-6 text-gray-400 transition-transform ${expandedId === guest._id ? "rotate-180" : ""}`}
                                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                            {expandedId === guest._id && (
                                <div className="px-4 pb-4 border-t border-gray-100">
                                    <div className="mt-3 mb-3 flex items-center justify-between">
                                        {guest.reservedTableId
                                            ? <span className="bg-green-200 text-green-700 text-sm font-semibold px-4 py-2 rounded-full">
                                                {t("reservedLabel", { tableName: resolveTableLabel(guest.reservedTableId.name) })}
                                            </span>
                                            : <span className="bg-red-100 text-red-600 text-sm font-semibold px-4 py-2 rounded-full">
                                                {t("notReservedLabel")}
                                            </span>}
                                        <button onClick={() => setEditGuest(guest)}
                                            className="bg-gray-200 text-gray-700 hover:bg-gray-300 transition py-2 px-3 rounded-full">
                                            <FaPen />
                                        </button>
                                    </div>
                                    <button onClick={() => handleCopy(guest)}
                                        className="w-full flex items-center justify-center bg-blue-700 text-white hover:bg-blue-800 transition text-sm py-2.5 px-4 rounded-full shadow-sm">
                                        <FiCopy className="mr-2" /> {t("copyGuestInfo")}
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-3 text-center text-gray-500 py-10">{t("noGuestsFound")}</div>
                    )}
                </div>
            </div>

            {editGuest && (
                <GuestEditModal
                    guest={editGuest}
                    tables={tables}
                    onClose={() => setEditGuest(null)}
                    onMove={(guestId, tableId) => { onMove(guestId, tableId); setEditGuest(null); showNotif(t("guestMoved")); }}
                    onDelete={(guestId) => { onDelete(guestId); setEditGuest(null); showNotif(t("guestRemoved")); }}
                />
            )}
        </div>
    );
};

// ─── TABLES VIEW ──────────────────────────────────────────────────────────────

const DemoTables = ({ tables, setActiveForm, onToggleLock, onDeleteTable, showNotif }) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("public");
    const [confirm, setConfirm] = useState({ open: false, message: "", onConfirm: null });

    const publicTables = tables.filter(tbl => !tbl.isPrivateTable);
    const privateTables = tables.filter(tbl => tbl.isPrivateTable);

    const ask = (message, onConfirm) => setConfirm({ open: true, message, onConfirm });
    const closeConfirm = () => setConfirm({ open: false, message: "", onConfirm: null });

    const tblLabel = (tbl) => tbl.name.startsWith("demoPrivate") ? t(tbl.name) : tbl.name;

    const handleLock = (table) => {
        const action = table.isLocked ? t("unlock") : t("lock");
        ask(t("confirmLockUnlockTable", { action, tableName: tblLabel(table) }), () => {
            onToggleLock(table._id);
            showNotif(t("tableLockSuccess", { state: table.isLocked ? t("unlocked") : t("locked") }));
            closeConfirm();
        });
    };

    const handleDelete = (table) => {
        ask(t("demoConfirmDeleteTable", { tableName: t("tableLabel", { name: tblLabel(table) }) }), () => {
            onDeleteTable(table._id);
            showNotif(t("tableDeletedSuccess"));
            closeConfirm();
        });
    };

    return (
        <div className="pt-10 lg:pt-10 flex lg:ml-[300px] lg:mr-2">
            <div className="rounded-md w-full mt-2">
                <div className="flex justify-between lg:justify-around items-center mb-4">
                    <h1 className="text-3xl font-semibold">{t("seatingArrangement")}</h1>
                    <button onClick={() => setActiveForm("addTable")}
                        className="bg-gradient-to-r from-rose-500 to-pink-600 text-white p-2 rounded-xl shadow-lg hover:from-rose-600 hover:to-pink-700 transition">
                        <FaPlusSquare className="text-xl" />
                    </button>
                </div>

                <div className="flex items-center gap-4 justify-around border border-gray-500 rounded-lg bg-white mb-8">
                    {[["public", t("publicTables"), publicTables.length], ["private", t("privateTables"), privateTables.length]].map(([tab, label, count]) => (
                        <button key={tab} onClick={() => setActiveTab(tab)}
                            className={`relative p-2 flex items-center gap-2 text-xl transition ${activeTab === tab ? "text-black" : "text-gray-500"}`}>
                            {label} <span>{count}</span>
                            {activeTab === tab && <div className="absolute -bottom-[2px] left-0 right-0 h-[3px] bg-gray-700 rounded-full" />}
                        </button>
                    ))}
                </div>

                {tables.length === 0 && <p className="w-full text-center text-gray-500 text-lg py-10">{t("noTables")}</p>}

                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    {(activeTab === "public" ? publicTables : privateTables).map(table => (
                        <div key={table._id} className="flex flex-col bg-white border border-gray-500 rounded-xl p-3 shadow-md hover:shadow-lg transition-all">
                            <h2 className="text-2xl italic font-semibold capitalize">
                                {t("tableLabel", { name: tblLabel(table) })}
                            </h2>
                            <div className="flex justify-between items-center text-gray-700 mb-2 mt-1">
                                <div className="flex items-center gap-1">
                                    <FaUsers className="mr-1" />
                                    <span>{table.guests.length}/{t("seatsLabel", { seats: table.seats })}</span>
                                </div>
                                <div className="bg-green-200 text-green-800 text-sm px-3 py-1 rounded-full">
                                    {t("freeSeatsLabel", { free: table.seats - table.guests.length })}
                                </div>
                            </div>

                            <div className="mb-2 flex-1">
                                {table.guests.length > 0 ? (
                                    <div className="space-y-1">
                                        {table.guests.map((g, i) => (
                                            <div key={i} className="flex items-center gap-2 text-gray-800">
                                                <FaUser className="shrink-0 text-sm" />
                                                <span>{cap(g.fullName)}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-sm">{t("noGuestsAtTable")}</p>
                                )}
                            </div>

                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-100">
                                <div className="text-sm">
                                    {table.seats === table.guests.length
                                        ? <span className="text-green-600 font-semibold">{t("tableFull")}</span>
                                        : table.isLocked
                                            ? <span className="text-red-500">{t("locked")}</span>
                                            : <span className="text-green-500">{t("unlocked")}</span>}
                                    {table.isPrivateTable && (
                                        <span className="ml-2 text-rose-500 font-semibold">{t("privateTable")}</span>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleLock(table)}
                                        className={`border p-2 rounded-lg transition ${!table.isLocked
                                            ? "border-green-300 text-green-500 hover:bg-green-50"
                                            : "border-red-300 text-red-500 hover:bg-red-50"
                                            }`}>
                                        {table.isLocked ? <FaLock /> : <FaUnlock />}
                                    </button>
                                    <button onClick={() => handleDelete(table)}
                                        className="border border-red-300 text-red-500 p-2 rounded-lg hover:bg-red-50 transition">
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <ConfirmModal isOpen={confirm.open} message={confirm.message} onConfirm={confirm.onConfirm} onClose={closeConfirm} />
        </div>
    );
};

// ─── ADD GUEST ────────────────────────────────────────────────────────────────

const DemoAddGuest = ({ onAdd, showNotif }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ fullName: "", gender: "" });
    const [errors, setErrors] = useState({});
    const [openDropdown, setOpenDropdown] = useState(false);

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};
        const nameRegex = /^[a-zA-ZčćđšžČĆĐŠŽäöüÄÖÜéèêëàâùûîïôœæ\s'-]+$/;
        if (!formData.fullName.trim()) newErrors.fullName = t("fullNameRequired");
        else if (!nameRegex.test(formData.fullName.trim())) newErrors.fullName = t("fullNameInvalid");
        else if (formData.fullName.trim().split(/\s+/).length < 2) newErrors.fullName = t("fullNameTwoWords");
        if (!formData.gender) newErrors.gender = t("genderRequired");
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

        onAdd({
            _id: "demo_" + Date.now(),
            fullName: formData.fullName.trim(),
            gender: formData.gender,
            password: Math.random().toString(36).substring(2, 8).toUpperCase(),
            reservedTableId: null,
            reservationCancelCount: 0,
            onLockedTable: false,
            weddingId: "demo",
        });
        showNotif(t("guestAddedSuccess"));
        setFormData({ fullName: "", gender: "" });
        setErrors({});
    };

    return (
        <div className="flex justify-center pt-10 lg:pt-10 pb-10 lg:ml-[300px]">
            <div className="p-4 rounded-lg w-full max-w-2xl">
                <h2 className="text-2xl font-semibold text-center mb-5 flex items-center justify-center gap-2">
                    <FaUserPlus className="text-gray-700" /> {t("addGuestTitle")}
                </h2>
                <p className="text-gray-600 mb-7">{t("addGuestSubtitle")}</p>
                <div className="flex flex-col space-y-6">
                    <div className="flex flex-col">
                        <label className="font-semibold mb-1">{t("fullName")} *</label>
                        <input type="text" value={formData.fullName}
                            onChange={e => { setFormData(p => ({ ...p, fullName: e.target.value })); setErrors(p => ({ ...p, fullName: "" })); }}
                            className={`border rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-300 ${errors.fullName ? "border-red-500" : "border-gray-400"}`} />
                        {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
                    </div>
                    <div className="flex flex-col relative">
                        <label className="font-semibold mb-1">{t("gender")} *</label>
                        <div onClick={() => setOpenDropdown(v => !v)}
                            className={`border rounded-xl px-4 py-2 flex justify-between items-center cursor-pointer bg-white ${errors.gender ? "border-red-500" : "border-gray-400"}`}>
                            <span className={formData.gender ? "text-gray-800" : "text-gray-400"}>
                                {formData.gender === "male" ? t("male") : formData.gender === "female" ? t("female") : t("selectGender")}
                            </span>
                            {openDropdown ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                        {openDropdown && (
                            <div className="absolute top-full z-50 w-full bg-white border border-gray-400 rounded-xl mt-1 overflow-hidden">
                                {[["male", t("male")], ["female", t("female")]].map(([v, l]) => (
                                    <button key={v} type="button"
                                        onClick={() => { setFormData(p => ({ ...p, gender: v })); setOpenDropdown(false); setErrors(p => ({ ...p, gender: "" })); }}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-100">{l}</button>
                                ))}
                            </div>
                        )}
                        {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender}</p>}
                    </div>
                    <button onClick={handleSubmit}
                        className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 rounded-xl font-medium hover:from-rose-600 hover:to-pink-700 transition shadow-lg">
                        {t("addGuestButton")}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── ADD TABLE ────────────────────────────────────────────────────────────────

const DemoAddTable = ({ onAdd, showNotif }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({ name: "", seats: "", isPrivateTable: false });
    const [errors, setErrors] = useState({});

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {};
        const nameRegex = /^[a-zA-Z0-9čćđšžČĆĐŠŽäöüÄÖÜéèêëàâùûîïôœæ\s-]+$/;
        if (!formData.name.trim()) newErrors.name = t("tableNameRequired");
        else if (!nameRegex.test(formData.name.trim())) newErrors.name = t("tableNameInvalid");
        const seats = parseInt(formData.seats, 10);
        if (!formData.seats) newErrors.seats = t("tableSeatsRequired");
        else if (isNaN(seats) || seats < 1 || seats > 100) newErrors.seats = t("tableSeatsInvalid");
        if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

        onAdd({ _id: "demo_t_" + Date.now(), name: formData.name.trim(), seats, isLocked: false, isPrivateTable: formData.isPrivateTable, guests: [] });
        showNotif(t("tableAddedSuccess"));
        setFormData({ name: "", seats: "", isPrivateTable: false });
        setErrors({});
    };

    return (
        <div className="flex justify-center pt-10 lg:pt-10 pb-10 lg:ml-[300px]">
            <div className="p-4 rounded-lg w-full max-w-2xl">
                <h2 className="text-2xl font-semibold text-center mb-5 flex items-center justify-center gap-2">
                    <FaPlusSquare className="text-gray-700" /> {t("addTableTitle")}
                </h2>
                <p className="text-gray-600 mb-7">{t("addTableSubtitle")}</p>
                <div className="flex flex-col space-y-6">
                    <div className="flex flex-col">
                        <label className="font-semibold mb-1">{t("tableNameLabel")} *</label>
                        <input type="text" value={formData.name}
                            onChange={e => { setFormData(p => ({ ...p, name: e.target.value })); setErrors(p => ({ ...p, name: "" })); }}
                            className={`border rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-300 ${errors.name ? "border-red-500" : "border-gray-400"}`} />
                        {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>
                    <div className="flex flex-col">
                        <label className="font-semibold mb-1">{t("tableSeatsLabel")} *</label>
                        <input type="number" min={1} max={100} value={formData.seats}
                            onChange={e => { setFormData(p => ({ ...p, seats: e.target.value })); setErrors(p => ({ ...p, seats: "" })); }}
                            className={`border rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-pink-300 ${errors.seats ? "border-red-500" : "border-gray-400"}`} />
                        {errors.seats && <p className="text-red-500 text-sm mt-1">{errors.seats}</p>}
                    </div>
                    <div className="flex items-center gap-3">
                        <input type="checkbox" id="priv" checked={formData.isPrivateTable}
                            onChange={e => setFormData(p => ({ ...p, isPrivateTable: e.target.checked }))}
                            className="w-5 h-5 accent-blue-700" />
                        <label htmlFor="priv" className="text-gray-800 font-medium">{t("privateTableLabel")}</label>
                    </div>
                    <button onClick={handleSubmit}
                        className="w-full bg-gradient-to-r from-rose-500 to-pink-600 text-white py-3 rounded-xl font-medium hover:from-rose-600 hover:to-pink-700 transition shadow-lg">
                        {t("addTableButton")}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── TABLES RESERVATION (guest view, mirrors TablesReservation.jsx) ───────────

const DemoTablesReservation = ({ tables, wedding, guest, setGuest, setTables, setGuests, showNotif, showError }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTableId, setSelectedTableId] = useState(null);
    const [message, setMessage] = useState("");
    const [isCancel, setIsCancel] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [limitMessage, setLimitMessage] = useState("");

    const lockDate = getLockDate(wedding);
    const editable = canEdit(wedding);
    const daysLeft = Math.ceil((lockDate - new Date()) / (1000 * 60 * 60 * 24));

    const tblLabel = (name) => name.startsWith("demoPrivate") ? t(name) : name;

    const handleReserveTable = (tableId) => {
        if (!editable) { showError(t("reservationLockedMessage")); return; }
        setSelectedTableId(tableId);
        setMessage(`${t("confirmReserveTable")}: ${tblLabel(tables.find(tbl => tbl._id === tableId).name)}?`);
        setIsModalOpen(true);
    };

    const handleCancelReservation = () => {
        if (!editable) { showError(t("reservationLockedMessage")); return; }
        setMessage(t("confirmCancelReservation"));
        setIsModalOpen(true);
        setIsCancel(true);
    };

    const handleConfirmAction = () => {
        setIsModalOpen(false);
        setIsLoading(true);

        if (isCancel) {
            // Remove reservation locally
            const oldTableId = guest.reservedTableId;
            const newCancelCount = (guest.reservationCancelCount || 0) + 1;
            setGuest(g => ({ ...g, reservedTableId: null, reservationCancelCount: newCancelCount }));
            setGuests(prev => prev.map(g => g._id === guest._id ? { ...g, reservedTableId: null, reservationCancelCount: newCancelCount } : g));
            setTables(prev => prev.map(tbl => {
                if (tbl._id === prevTableId) return { ...tbl, guests: tbl.guests.filter(g => g._id !== guest._id) };
                if (tbl._id === selectedTableId) {
                    // izbjegni duplikat
                    if (tbl.guests.some(g => g._id === guest._id)) return tbl;
                    return { ...tbl, guests: [...tbl.guests, newGuest] };
                }
                return tbl;
            }));
            showNotif(t("reservationCancelled"));
            setIsCancel(false);
            setIsLoading(false);
            return;
        }

        // Reserve seat locally
        const prevTableId = typeof guest.reservedTableId === "object" ? guest.reservedTableId?._id : guest.reservedTableId;
        const newGuest = { _id: guest._id, fullName: guest.fullName, gender: guest.gender };
        const newTable = tables.find(tbl => tbl._id === selectedTableId);

        setGuest(g => ({ ...g, reservedTableId: selectedTableId }));
        setGuests(prev => prev.map(g => g._id === guest._id
            ? { ...g, reservedTableId: newTable ? { _id: newTable._id, name: newTable.name } : null }
            : g
        ));
        setTables(prev => prev.map(tbl => {
            if (tbl._id === prevTableId) return { ...tbl, guests: tbl.guests.filter(g => g._id !== guest._id) };
            if (tbl._id === selectedTableId) return { ...tbl, guests: [...tbl.guests, newGuest] };
            return tbl;
        }));
        showNotif(t("tableReservedSuccess"));
        setSelectedTableId(null);
        setIsLoading(false);
    };

    return (
        <div className="flex w-full p-4">
            <div className="rounded-md w-full">
                <div className="lg:flex lg:justify-between lg:gap-5 lg:items-center flex-wrap">
                    <h2 className="text-xl font-semibold mb-2">
                        {t("welcomeGuest", { name: cap(guest.fullName) })}
                    </h2>

                    {guest.reservedTableId
                        ? <h2 className="text-xl mb-2">
                            {t("currentTable", { name: tblLabel(tables.find(tbl => tbl._id === guest.reservedTableId)?.name || "") })}
                        </h2>
                        : <h2 className="text-xl mb-2">{t("noTableReserved")}</h2>}

                    {/* Countdown / lock banner — only shown when no reservation yet */}
                    {!guest.reservedTableId && (
                        editable ? (
                            <div className={`p-4 mb-4 rounded-lg border-l-4 ${daysLeft <= 3 ? "bg-orange-50 border-orange-500" : "bg-blue-50 border-blue-500"}`}>
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">⏰</span>
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-1">{t("reservationDeadline")}</h4>
                                        <p className={daysLeft <= 3 ? "text-orange-700" : "text-blue-700"}>
                                            {daysLeft > 0
                                                ? t("daysUntilLock", { days: daysLeft, date: lockDate.toLocaleDateString() })
                                                : t("lastDayToReserve")}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4 rounded-lg">
                                <div className="flex items-start gap-3">
                                    <span className="text-2xl">🔒</span>
                                    <div>
                                        <h4 className="font-semibold text-red-800 mb-1">{t("reservationsLocked")}</h4>
                                        <p className="text-red-700">{t("reservationLockedMessage")}</p>
                                    </div>
                                </div>
                            </div>
                        )
                    )}

                    {/* Cancel count warning */}
                    {guest.reservationCancelCount >= 2 && (
                        <div className={`flex flex-wrap justify-between text-sm md:text-xl font-medium px-4 py-2 rounded-xl mb-4 ${guest.reservationCancelCount >= 5 ? "bg-red-200 text-red-800"
                            : guest.reservationCancelCount >= 3 ? "bg-yellow-200 text-yellow-800"
                                : "bg-green-100 text-green-800"}`}>
                            {t("reservationCancelInfo", { count: guest.reservationCancelCount, remaining: 5 - guest.reservationCancelCount })}
                            {guest.reservationCancelCount >= 5 && <p>{t("reservationLimitReached")}</p>}
                        </div>
                    )}
                    {limitMessage && <p className="text-red-600 text-sm mb-4">{limitMessage}</p>}
                </div>

                {tables.length === 0 && <p className="w-full text-center text-gray-500 text-lg py-10">{t("noTables")}</p>}

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {tables.map(table => (
                        <div key={table._id}
                            className={`flex flex-col ${guest.reservedTableId === table._id ? "bg-blue-300 border-blue-500" : "bg-white border border-gray-500"} rounded-xl p-4 shadow-md hover:shadow-lg transition-all border`}>
                            <div className="flex justify-between items-start mb-2">
                                <h2 className="text-2xl italic font-semibold capitalize">
                                    {t("tableLabel", { name: tblLabel(table.name) })}
                                </h2>
                            </div>

                            <div className="flex justify-between items-center gap-4 text-gray-800 mb-4 mt-2">
                                <div className="flex items-center">
                                    <FaUsers className="mr-2" />
                                    <span>{table.guests.length}/{t("seatsLabel", { seats: table.seats })}</span>
                                </div>
                                <div className="bg-green-200 text-green-900 text-sm px-3 py-1 rounded-full">
                                    {t("freeSeatsLabel", { free: table.seats - table.guests.length })}
                                </div>
                            </div>

                            <div className="flex-1 mb-6">
                                <h1 className="mb-2">{t("guestsAtTable")}</h1>
                                {table.guests.length > 0 ? (
                                    <ul className="space-y-2">
                                        {table.guests.map((g, i) => (
                                            <li key={i} className="flex items-center gap-2 text-gray-900">
                                                <FaUser /> {cap(g.fullName)}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-center text-gray-500">{t("noGuestsAtTable")}</p>
                                )}
                            </div>

                            {guest.reservationCancelCount < 5 && (
                                <div>
                                    {/* Can reserve */}
                                    {table.guests.length < table.seats
                                        && table._id !== guest.reservedTableId
                                        && !table.isLocked
                                        && !guest.onLockedTable && (
                                            <button onClick={() => handleReserveTable(table._id)}
                                                disabled={!editable}
                                                className={`submitButton w-full ${!editable ? "opacity-50 cursor-not-allowed" : ""}`}>
                                                {isLoading && selectedTableId === table._id ? "…" : t("chooseThisTable")}
                                            </button>
                                        )}
                                    {/* Current table */}
                                    {guest.reservedTableId === table._id && table.guests.length < table.seats && (
                                        <div className="flex flex-col items-center bg-white text-gray-800 p-3 rounded-xl gap-2">
                                            <span>{t("yourCurrentTable")}</span>
                                            {guest.reservationCancelCount < 4 && !guest.onLockedTable && (
                                                <button onClick={() => handleCancelReservation()}
                                                    className="text-red-600 text-sm underline hover:text-red-800 transition">
                                                    {t("cancelReservation")}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    {/* Locked */}
                                    {table.isLocked && table.guests.length < table.seats && (
                                        <div className="flex items-center pl-4 w-full h-12 bg-red-200 text-red-700 p-2 rounded-xl mt-4">
                                            {t("tableLocked")}
                                        </div>
                                    )}
                                    {/* Full */}
                                    {table.guests.length >= table.seats && table._id !== guest.reservedTableId && (
                                        <div className="flex items-center pl-4 w-full h-12 bg-green-400 text-gray-800 p-2 rounded-xl">
                                            {t("tableFull")}
                                        </div>
                                    )}
                                    {/* Full + current */}
                                    {table.guests.length >= table.seats && guest.reservedTableId === table._id && (
                                        <div className="flex flex-col items-center bg-white text-gray-800 p-3 rounded-xl gap-3">
                                            <span>{t("tableFullYouAreHere")}</span>
                                            {guest.reservationCancelCount < 4 && !guest.onLockedTable && editable && (
                                                <button onClick={() => handleCancelReservation()}
                                                    className="text-red-600 text-sm underline hover:text-red-800 transition">
                                                    {t("cancelReservation")}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <ConfirmModal isOpen={isModalOpen} message={message} onConfirm={handleConfirmAction} onClose={() => { setIsModalOpen(false); setIsCancel(false); setSelectedTableId(null); }} />
        </div>
    );
};

// ─── GUEST DEMO TAB (mirrors ReservationPage.jsx) ─────────────────────────────

const GuestDemoTab = ({ tables, setTables, setGuests, isLoggedIn, setIsLoggedIn, guest, setGuest }) => {
    const { t, i18n } = useTranslation();
    const [code, setCode] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [codeError, setCodeError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [notification, setNotification] = useState(null);
    const [notifIsError, setNotifIsError] = useState(false);

    const publicTables = tables.filter(tbl => !tbl.isPrivateTable);

    const showNotif = (msg) => { setNotifIsError(false); setNotification(msg); setTimeout(() => setNotification(null), 3000); };
    const showError = (msg) => { setNotifIsError(true); setNotification(msg); setTimeout(() => setNotification(null), 3000); };

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        setTimeout(() => {
            if (code.trim().toLowerCase() === "test1") {
                setIsLoggedIn(true);
                setCodeError(false);
            } else {
                setCodeError(true);
            }
            setLoading(false);
        }, 600);
    };

    const handleLogout = () => {
        setIsLoggedIn(false);
        setGuest({ ...DEMO_LOGGED_GUEST });
        setCode("");
        setShowLogoutConfirm(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-cyan-300 via-white to-pink-400 flex flex-col">
            {/* Nav — mirrors ReservationPage */}
            <div className="flex justify-between items-center px-4 py-4 lg:px-10 relative">
                <Link to="/" className="flex items-center">
                    <img src="/logo2.png" className="w-12 h-12" alt="Weddinfy" />
                    <h1 className="text-xl font-semibold text-gray-800">{t("appName")}</h1>
                </Link>
                <div className="flex items-center space-x-4">
                    {/* Language switcher */}
                    <div className="relative" tabIndex={0} onBlur={() => setOpenDropdown(null)}>
                        <div className="bg-white border border-gray-500 rounded-xl px-2 cursor-pointer flex items-center"
                            onClick={() => setOpenDropdown(prev => prev === "language" ? null : "language")}>
                            <span>
                                {i18n.language === "hr" && "🇭🇷"}
                                {i18n.language === "en" && "🇬🇧"}
                                {i18n.language === "de" && "🇩🇪"}
                            </span>
                            {openDropdown === "language" ? <FaChevronUp className="ml-2 text-gray-600" /> : <FaChevronDown className="ml-2 text-gray-600" />}
                        </div>
                        {openDropdown === "language" && (
                            <div className="absolute right-0 mt-1 z-50 w-full min-w-[130px] bg-white border border-gray-500 rounded-xl shadow-lg">
                                {[{ code: "hr", label: "Hrvatski", flag: "🇭🇷" }, { code: "en", label: "English", flag: "🇬🇧" }, { code: "de", label: "Deutsch", flag: "🇩🇪" }].map(lang => (
                                    <button key={lang.code} type="button"
                                        onMouseDown={() => { i18n.changeLanguage(lang.code); localStorage.setItem("lang", lang.code); setOpenDropdown(null); }}
                                        className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-xl flex items-center gap-2">
                                        <span className="text-xl">{lang.flag}</span>
                                        <span>{lang.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {isLoggedIn && (
                        <button onClick={() => setShowLogoutConfirm(true)}
                            className="text-red-600 hover:text-red-800 flex items-center space-x-2 py-2">
                            <FaLogout className="text-lg" />
                            <span>{t("logout")}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Logout confirm */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
                    <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm text-center space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800">{t("logoutConfirmTitle")}</h3>
                        <div className="flex justify-end space-x-2 mt-4">
                            <button onClick={handleLogout} className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 transition">{t("logout")}</button>
                            <button onClick={() => setShowLogoutConfirm(false)} className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition">{t("cancel")}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Wedding header */}
            <div className="flex-wrap flex-col w-full">
                <h1 className="text-center text-2xl lg:text-4xl font-bold text-gray-800 capitalize mb-5">
                    {DEMO_WEDDING.brideName} & {DEMO_WEDDING.groomName}
                </h1>
                <div className="flex items-center justify-around">
                    <span className="flex items-center gap-1 lg:text-xl">
                        <FaCalendarAlt className="text-sm lg:text-lg" />
                        {new Date(DEMO_WEDDING.date).toLocaleDateString("hr-HR")}
                    </span>
                    <span className="flex items-center gap-1 lg:text-xl capitalize">
                        <FaMapMarkerAlt className="text-sm lg:text-lg" />
                        {DEMO_WEDDING.location}, {DEMO_WEDDING.city}
                    </span>
                </div>
            </div>

            {/* Login form or tables */}
            {!isLoggedIn ? (
                <div className="w-full flex flex-col lg:flex-row items-center justify-center pb-10">
                    <div className="pb-10 p-4 sm:p-10 w-full lg:w-1/2 max-w-screen-md flex flex-col items-center justify-center px-4">
                        <div className="flex flex-col items-center">
                            <h2 className="text-xl font-bold text-gray-800">{t("welcomeTitle")}</h2>
                            <p className="text-gray-700 mt-1">{t("welcomeSubtitle")}</p>
                        </div>
                        <form onSubmit={handleSubmit} className="mt-6 w-full">
                            <div className="relative mt-7">
                                <label>
                                    {t("codeLabel")} · <span className="text-gray-700 font-normal">{t("demoCodeLabel")}</span>
                                </label>
                                <FaLock className="absolute left-3 top-[calc(50%+10px)] -translate-y-1/2 text-gray-500" />
                                <input
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    maxLength={50}
                                    value={code}
                                    onChange={e => { setCode(e.target.value); setCodeError(false); }}
                                    className={`inputWedding ${codeError ? "!border-red-500" : ""}`}
                                    placeholder="test1"
                                />
                                <button type="button" onClick={() => setShowPassword(v => !v)} className="absolute top-[calc(50%+10px)] right-4 -translate-y-1/2">
                                    {showPassword ? <IoEyeOutline size={20} /> : <IoEyeOffOutline size={20} />}
                                </button>
                            </div>
                            {codeError && <p className="bg-red-100 rounded-lg p-2 mt-4 text-red-700 text-sm text-center">{t("invalidCredentials")}</p>}

                            <button type="submit" className="submitButton mt-10 w-full" disabled={loading}>
                                {loading ? "…" : t("loginButton2")}
                            </button>
                        </form>
                        {/* <p className="text text-gray-400 mt-4">{t("demoCodeHint")}: <span className="font-mono font-bold">test1</span></p> */}
                        <div className="mt-6 pt-4 text-center text-gray-800">
                            <Link to="/" className="inline-flex items-center gap-1 cursor-pointer underline">
                                <FaUser /> {t("homePage")}
                            </Link>
                        </div>
                    </div>
                    <div className="flex lg:w-1/2 lg:mt-7 flex-col items-center text-center px-4 space-y-6">
                        <img src="/img2.jpg" alt="Vjenčanje" className="w-full max-w-md rounded-lg" />
                    </div>
                </div>
            ) : (
                <DemoTablesReservation
                    tables={publicTables}
                    wedding={DEMO_WEDDING}
                    guest={guest}
                    setGuest={setGuest}
                    setTables={setTables}
                    setGuests={setGuests}
                    showNotif={showNotif}
                    showError={showError}
                />
            )}

            {notification && <Notification message={notification} onClose={() => setNotification(null)} isError={notifIsError} />}
        </div>
    );
};

// ─── MAIN DEMO PAGE ───────────────────────────────────────────────────────────

const DemoPage = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("admin");
    const [adminForm, setAdminForm] = useState("guests");
    const [tables, setTables] = useState(DEMO_TABLES_INIT);
    const [guests, setGuests] = useState(DEMO_GUESTS_INIT);
    const [notification, setNotification] = useState(null);
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    // Guest login state — lifted here so it survives tab switching
    const [guestIsLoggedIn, setGuestIsLoggedIn] = useState(false);
    const [guestData, setGuestData] = useState({ ...DEMO_LOGGED_GUEST });

    // Sync onLockedTable — if admin locks/unlocks the table the demo guest is sitting at
    useEffect(() => {
        if (!guestData.reservedTableId) {
            setGuestData(prev => ({ ...prev, onLockedTable: false }));
            return;
        }
        const guestTable = tables.find(tbl => tbl._id === guestData.reservedTableId);
        if (guestTable) {
            setGuestData(prev => ({ ...prev, onLockedTable: guestTable.isLocked }));
        }
    }, [tables, guestData.reservedTableId]);

    const showNotif = (msg) => { setNotification(msg); setTimeout(() => setNotification(null), 3000); };

    const handleMoveGuest = (guestId, tableId) => {
        const guest = guests.find(g => g._id === guestId);
        const newTable = tableId ? tables.find(t => t._id === tableId) : null;
        setGuests(prev => prev.map(g => g._id !== guestId ? g : { ...g, reservedTableId: newTable ? { _id: newTable._id, name: newTable.name } : null }));
        setTables(prev => prev.map(tbl => {
            if (guest?.reservedTableId?._id === tbl._id) return { ...tbl, guests: tbl.guests.filter(g => g._id !== guestId) };
            if (tbl._id === tableId) return { ...tbl, guests: [...tbl.guests, { _id: guestId, fullName: guest.fullName, gender: guest.gender }] };
            return tbl;
        }));
        if (guestId === guestData._id) {
            setGuestData(prev => ({ ...prev, reservedTableId: tableId || null }));
        }
    };

    const handleDeleteGuest = (guestId) => {
        const guest = guests.find(g => g._id === guestId);
        setGuests(prev => prev.filter(g => g._id !== guestId));
        if (guest?.reservedTableId) {
            setTables(prev => prev.map(tbl => tbl._id === guest.reservedTableId._id ? { ...tbl, guests: tbl.guests.filter(g => g._id !== guestId) } : tbl));
        }
        if (guestId === guestData._id) {
            setGuestData(prev => ({ ...prev, reservedTableId: null }));
        }
    };

    const handleToggleLock = (tableId) => {
        setTables(prev => prev.map(tbl => tbl._id === tableId ? { ...tbl, isLocked: !tbl.isLocked } : tbl));
    };

    const handleDeleteTable = (tableId) => {
        const table = tables.find(tbl => tbl._id === tableId);
        const affected = new Set((table?.guests || []).map(g => g._id));
        setTables(prev => prev.filter(tbl => tbl._id !== tableId));
        setGuests(prev => prev.map(g => affected.has(g._id) ? { ...g, reservedTableId: null } : g));
    };

    const handleAddGuest = (guest) => { setGuests(prev => [...prev, guest]); setAdminForm("guests"); };
    const handleAddTable = (table) => { setTables(prev => [...prev, table]); setAdminForm("tables"); };

    return (
        <div className="min-h-screen">
            <DemoBanner />

            {/* Tab switcher */}
            <div className="fixed top-10 left-0 right-0 z-30 bg-white border-b border-gray-200 shadow-sm">
                <div className="max-w-2xl mx-auto flex">
                    {[["admin", `👑 ${t("demoTabAdmin")}`], ["guest", `👤 ${t("demoTabGuest")}`]].map(([tab, label]) => (
                        <button key={tab} onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-3 text-base font-medium transition border-b-2 ${activeTab === tab ? "border-rose-500 text-rose-500" : "border-transparent text-gray-500 hover:text-gray-700"
                                }`}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="pt-[88px]">
                {activeTab === "guest" && (
                    <GuestDemoTab
                        tables={tables}
                        setTables={setTables}
                        setGuests={setGuests}
                        isLoggedIn={guestIsLoggedIn}
                        setIsLoggedIn={setGuestIsLoggedIn}
                        guest={guestData}
                        setGuest={setGuestData}
                    />
                )}

                {activeTab === "admin" && (
                    <div className="min-h-screen bg-gradient-to-br from-cyan-300 via-white to-pink-400">
                        <DemoSidebar activeForm={adminForm} setActiveForm={setAdminForm} guests={guests} tables={tables} isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen} />
                        <div className="p-4">
                            {adminForm === "guests" && <DemoGuestList guests={guests} tables={tables} setActiveForm={setAdminForm} onMove={handleMoveGuest} onDelete={handleDeleteGuest} showNotif={showNotif} />}
                            {adminForm === "tables" && <DemoTables tables={tables} setActiveForm={setAdminForm} onToggleLock={handleToggleLock} onDeleteTable={handleDeleteTable} showNotif={showNotif} />}
                            {adminForm === "addGuest" && <DemoAddGuest onAdd={handleAddGuest} showNotif={showNotif} />}
                            {adminForm === "addTable" && <DemoAddTable onAdd={handleAddTable} showNotif={showNotif} />}
                        </div>
                    </div>
                )}
            </div>

            {notification && <Notification message={notification} onClose={() => setNotification(null)} />}
        </div>
    );
};

export default DemoPage;
